import type {
  CreateWorkspaceDirectInvitationResponse,
  GetWorkspaceInvitationsResponse,
  MemberDeviceUsageResponseSchema,
  UnactivatedMembersResponseSchema,
  UpdateWorkspaceMembershipConfigRequest,
  UpdateWorkspaceMembershipConfigResponse,
  UpdateWorkspaceMembershipResponse,
  WorkspaceInvitation,
  WorkspaceRole,
} from "../web-client/api/data-contracts";
import * as operations from "../web-client/api/operations";
import type { Transport } from "../web-client/transport";

export const memberKeys = {
  deviceUsage: (workspaceId: string) => ["members", "device-usage", workspaceId] as const,
  unactivated: (workspaceId: string) => ["members", "unactivated", workspaceId] as const,
  invitations: (workspaceId: string) => ["members", "invitations", workspaceId] as const,
};

export function getPersonalDeviceMembers(
  transport: Transport,
  { workspaceId }: { workspaceId: string },
): Promise<MemberDeviceUsageResponseSchema> {
  return operations.getWorkspacesMembershipsDeviceUsage(transport, { workspaceId });
}

export function getUnactivatedMembers(
  transport: Transport,
  { workspaceId }: { workspaceId: string },
): Promise<UnactivatedMembersResponseSchema> {
  return operations.getWorkspacesMembershipsUnactivated(transport, { workspaceId });
}

export function getWorkspaceInvitations(
  transport: Transport,
  { workspaceId }: { workspaceId: string },
): Promise<GetWorkspaceInvitationsResponse> {
  return operations.getWorkspacesInvitations(transport, { workspaceId });
}

export function createWorkspaceInvitations(
  transport: Transport,
  {
    workspaceId,
    emails,
    phoneNumbers,
    sendEmail,
    invitations,
  }: {
    workspaceId: string;
    emails?: string[];
    phoneNumbers?: string[];
    sendEmail?: boolean;
    invitations?: WorkspaceInvitation[];
  },
): Promise<CreateWorkspaceDirectInvitationResponse> {
  // Don't put empty email/phone values into the payload — they break the validator.
  const cleanInvitations = invitations?.map((invitation) => ({
    name: invitation.name,
    role: invitation.role,
    sendEmail: invitation.sendEmail,
    ...(invitation.email ? { email: invitation.email } : {}),
    ...(invitation.phoneNumber ? { phoneNumber: invitation.phoneNumber } : {}),
  }));
  return operations.postWorkspacesDirectInvitations(transport, {
    workspaceId,
    body: { emails, phoneNumbers, sendEmail, invitations: cleanInvitations },
  });
}

export function updateWorkspaceMember(
  transport: Transport,
  {
    workspaceId,
    workspaceMembershipIds,
    role,
  }: { workspaceId: string; workspaceMembershipIds: string[]; role: WorkspaceRole },
): Promise<UpdateWorkspaceMembershipResponse> {
  return operations.patchWorkspacesWorkspaceMembershipsUpdate(transport, {
    workspaceId,
    body: { workspaceMembershipIds, role },
  });
}

export function removeWorkspaceMember(
  transport: Transport,
  { workspaceId, workspaceMembershipIds }: { workspaceId: string; workspaceMembershipIds: string[] },
): Promise<UpdateWorkspaceMembershipResponse> {
  return operations.deleteWorkspacesWorkspaceMembershipsRemove(transport, {
    workspaceId,
    body: { workspaceMembershipIds, status: "inactive" },
  });
}

export function updateMembershipConfig(
  transport: Transport,
  {
    workspaceId,
    membershipId,
    config,
  }: { workspaceId: string; membershipId: string; config: UpdateWorkspaceMembershipConfigRequest },
): Promise<UpdateWorkspaceMembershipConfigResponse> {
  return operations.patchWorkspacesMembershipsConfig(transport, { workspaceId, membershipId, body: config });
}
